/**
 * Client Date Reminders — shared defaults + upcoming helpers (Admin-only).
 * Keep institutional seed in sync with functions/clientDateReminders.js
 */

export const DEFAULT_CLIENT_DATE_REMINDERS = {
  enabled: true,
  recipient_email: "admin@fidusfinance.com",
  timezone: "Europe/London",
  send_hour_local: 8,
  offsets_days: [1, 0],
  lookahead_days: 30,
  types: {
    birthday: { enabled: true },
    account_anniversary: { enabled: true },
    custom: { enabled: true },
    institutional: { enabled: true },
  },
  institutional_events: null, // filled by merge with seed
};

/** Seed catalogue — toggles/tips overridable in admin_settings.client_date_reminders */
export const INSTITUTIONAL_EVENTS_SEED = [
  {
    id: "new_year",
    label: "New Year's Day",
    month: 1,
    day: 1,
    enabled: true,
    tip_en: "Warm new-year note to individual clients — keep it short and personal.",
  },
  {
    id: "valentines",
    label: "Valentine's Day",
    month: 2,
    day: 14,
    enabled: true,
    tip_en: "Optional light touch for long-standing individual relationships — never spammy.",
  },
  {
    id: "mothers_day_uk",
    label: "Mother's Day (UK)",
    variable: "last_sunday_march",
    enabled: true,
    tip_en: "UK Mother's Day — last Sunday of March. Consider a discreet note where appropriate.",
  },
  {
    id: "fathers_day_uk",
    label: "Father's Day (UK)",
    variable: "third_sunday_june",
    enabled: true,
    tip_en: "UK Father's Day — third Sunday of June.",
  },
  {
    id: "chinese_new_year",
    label: "Chinese New Year (tip)",
    month: 1,
    day: 28,
    enabled: false,
    tip_en: "Lunar date moves each year — update month/day in settings when you confirm the date.",
  },
  {
    id: "christmas",
    label: "Christmas Day",
    month: 12,
    day: 25,
    enabled: true,
    tip_en: "Seasonal greetings for individual clients (manual send).",
  },
  {
    id: "boxing_day",
    label: "Boxing Day",
    month: 12,
    day: 26,
    enabled: false,
    tip_en: "Optional follow-up after Christmas.",
  },
];

export function mergeClientDateReminderConfig(raw) {
  const base = { ...DEFAULT_CLIENT_DATE_REMINDERS, ...(raw || {}) };
  base.types = {
    ...DEFAULT_CLIENT_DATE_REMINDERS.types,
    ...(raw?.types || {}),
  };
  Object.keys(DEFAULT_CLIENT_DATE_REMINDERS.types).forEach((k) => {
    base.types[k] = {
      enabled: true,
      ...(DEFAULT_CLIENT_DATE_REMINDERS.types[k] || {}),
      ...(raw?.types?.[k] || {}),
    };
  });

  const seedById = Object.fromEntries(INSTITUTIONAL_EVENTS_SEED.map((e) => [e.id, e]));
  const fromSettings = Array.isArray(raw?.institutional_events) ? raw.institutional_events : [];
  const fromSettingsById = Object.fromEntries(fromSettings.map((e) => [e.id, e]));
  const ids = [
    ...INSTITUTIONAL_EVENTS_SEED.map((e) => e.id),
    ...fromSettings.map((e) => e.id).filter((id) => !seedById[id]),
  ];
  base.institutional_events = ids.map((id) => ({
    ...(seedById[id] || {}),
    ...(fromSettingsById[id] || {}),
    id,
  }));

  if (!Array.isArray(base.offsets_days) || base.offsets_days.length === 0) {
    base.offsets_days = [1, 0];
  }
  base.offsets_days = [...new Set(base.offsets_days.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n >= 0))].sort(
    (a, b) => b - a
  );
  if (!base.recipient_email) base.recipient_email = "admin@fidusfinance.com";
  if (!Number.isFinite(Number(base.lookahead_days))) base.lookahead_days = 30;
  if (!Number.isFinite(Number(base.send_hour_local))) base.send_hour_local = 8;
  return base;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

/** YYYY-MM-DD in Europe/London for a Date */
export function londonYmd(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function addDaysYmd(ymd, days) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

export function mmDdFromYmd(ymd) {
  if (!ymd || typeof ymd !== "string" || ymd.length < 10) return null;
  return ymd.slice(5, 10);
}

export function parseToYmd(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  try {
    const d = value.toDate ? value.toDate() : value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return londonYmd(d);
  } catch {
    return null;
  }
}

function nthSundayOfMonth(year, monthIndex0, n) {
  let count = 0;
  for (let day = 1; day <= 31; day++) {
    const dt = new Date(Date.UTC(year, monthIndex0, day));
    if (dt.getUTCMonth() !== monthIndex0) break;
    if (dt.getUTCDay() === 0) {
      count += 1;
      if (count === n) return `${year}-${pad2(monthIndex0 + 1)}-${pad2(day)}`;
    }
  }
  return null;
}

function lastSundayOfMonth(year, monthIndex0) {
  for (let day = 31; day >= 1; day--) {
    const dt = new Date(Date.UTC(year, monthIndex0, day));
    if (dt.getUTCMonth() !== monthIndex0) continue;
    if (dt.getUTCDay() === 0) return `${year}-${pad2(monthIndex0 + 1)}-${pad2(day)}`;
  }
  return null;
}

/** Resolve institutional event to YYYY-MM-DD for a given calendar year */
export function resolveInstitutionalDate(event, year) {
  if (!event) return null;
  if (event.variable === "last_sunday_march") return lastSundayOfMonth(year, 2);
  if (event.variable === "third_sunday_june") return nthSundayOfMonth(year, 5, 3);
  const month = Number(event.month);
  const day = Number(event.day);
  if (!month || !day) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

export function isIndividualClient(user) {
  const t = user?.user_type;
  return t === undefined || t === null || Number(t) === 0;
}

function yearsBetween(fromYmd, onYmd) {
  if (!fromYmd || !onYmd) return null;
  const [fy, fm, fd] = fromYmd.split("-").map(Number);
  const [ty, tm, td] = onYmd.split("-").map(Number);
  let years = ty - fy;
  if (tm < fm || (tm === fm && td < fd)) years -= 1;
  return years >= 0 ? years : null;
}

/**
 * Build upcoming items for the next lookaheadDays (from London today).
 * @param {object[]} clients - user docs with id
 * @param {object} config - merged client_date_reminders
 */
export function buildUpcomingItems(clients, config, now = new Date()) {
  const cfg = mergeClientDateReminderConfig(config);
  const today = londonYmd(now);
  const lookahead = Math.max(1, Number(cfg.lookahead_days) || 30);
  const items = [];

  const individuals = (clients || []).filter(
    (c) => Number(c.role) === 1 && Number(c.status) === 1 && isIndividualClient(c)
  );

  for (let i = 0; i <= lookahead; i++) {
    const ymd = addDaysYmd(today, i);
    const mmdd = mmDdFromYmd(ymd);
    const year = Number(ymd.slice(0, 4));

    if (cfg.types.birthday?.enabled !== false) {
      individuals.forEach((c) => {
        if (c.date_reminder_muted?.birthday) return;
        const dob = parseToYmd(c.birth_date);
        if (!dob || mmDdFromYmd(dob) !== mmdd) return;
        const age = yearsBetween(dob, ymd);
        items.push({
          key: `bday-${c.id}-${ymd}`,
          type: "birthday",
          date: ymd,
          days_until: i,
          user_id: c.id,
          full_name: c.full_name || "—",
          email: c.email || "",
          do_not_contact: c.do_not_contact === true,
          detail: age != null ? `Turning ${age}` : "Birthday",
          label: "Birthday",
        });
      });
    }

    if (cfg.types.account_anniversary?.enabled !== false) {
      individuals.forEach((c) => {
        if (c.date_reminder_muted?.account_anniversary) return;
        const reg = parseToYmd(c.registered_date);
        if (!reg || mmDdFromYmd(reg) !== mmdd) return;
        // Skip year 0 anniversary on signup day of same year if years===0 and we want "years with Fiddux"
        const years = yearsBetween(reg, ymd);
        if (years == null || years < 1) return;
        items.push({
          key: `acct-${c.id}-${ymd}`,
          type: "account_anniversary",
          date: ymd,
          days_until: i,
          user_id: c.id,
          full_name: c.full_name || "—",
          email: c.email || "",
          do_not_contact: c.do_not_contact === true,
          detail: `${years} year${years === 1 ? "" : "s"} with Fiddux`,
          label: "Account anniversary",
        });
      });
    }

    if (cfg.types.custom?.enabled !== false) {
      individuals.forEach((c) => {
        const list = Array.isArray(c.commemorative_dates) ? c.commemorative_dates : [];
        list.forEach((cd) => {
          if (!cd || cd.enabled === false) return;
          const m = Number(cd.month);
          const d = Number(cd.day);
          if (!m || !d) return;
          if (`${pad2(m)}-${pad2(d)}` !== mmdd) return;
          items.push({
            key: `custom-${c.id}-${cd.id || `${m}-${d}`}-${ymd}`,
            type: "custom",
            date: ymd,
            days_until: i,
            user_id: c.id,
            full_name: c.full_name || "—",
            email: c.email || "",
            do_not_contact: c.do_not_contact === true,
            detail: cd.label || "Custom date",
            label: cd.label || "Custom",
            custom_id: cd.id,
          });
        });
      });
    }

    if (cfg.types.institutional?.enabled !== false) {
      (cfg.institutional_events || []).forEach((ev) => {
        if (ev.enabled === false) return;
        const resolved = resolveInstitutionalDate(ev, year);
        if (!resolved || resolved !== ymd) return;
        items.push({
          key: `inst-${ev.id}-${ymd}`,
          type: "institutional",
          date: ymd,
          days_until: i,
          user_id: null,
          full_name: "—",
          email: "",
          do_not_contact: false,
          detail: ev.tip_en || "",
          label: ev.label || ev.id,
          institutional_id: ev.id,
        });
      });
    }
  }

  items.sort((a, b) => a.days_until - b.days_until || String(a.label).localeCompare(String(b.label)));
  return items;
}

export function newCustomDateId() {
  return `cd_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}
